import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';

const accelerationCauses = [
    {
        id: 'airflow',
        title: '1. Restricted Airflow',
        text: 'Engines need a steady supply of clean air. If the intake side is restricted by a dirty air filter, blocked intake path, or contaminated sensor, the engine cannot burn fuel properly, and the car starts feeling flat rather than sharp.'
    },
    {
        id: 'fuel',
        title: '2. Fuel Delivery Issues',
        text: 'If the engine is not getting enough fuel, it cannot build power. This usually becomes obvious on hills or during highway merging. Common causes include clogged injectors, a weak fuel pump, or a restricted fuel filter.'
    },
    {
        id: 'ignition',
        title: '3. Ignition System Problems',
        text: 'Even with the right air and fuel, the engine needs a strong spark. Tired spark plugs or faulty ignition coils lead to inconsistent combustion, causing hesitation or a jerky feeling when you press the gas pedal.'
    },
    {
        id: 'transmission',
        title: '4. Transmission or Clutch Issues',
        text: 'Sometimes the engine makes power normally, but it doesn’t reach the wheels efficiently. Transmission slip, worn clutches, or low transmission fluid can cause the engine to rev higher than usual while the car speeds up slowly.'
    }
];

const faqs = [
    {
        question: 'Can a dirty air filter really affect acceleration?',
        answer: 'Yes. A clogged air filter can significantly reduce airflow, making the engine feel weaker and less responsive, especially when you need extra power.'
    },
    {
        question: 'Why does my car rev higher but not speed up properly?',
        answer: 'That usually points to a transmission slip or a worn clutch in manual cars, rather than a direct engine power issue.'
    },
    {
        question: 'Can bad spark plugs cause poor acceleration?',
        answer: 'Yes. Worn or fouled spark plugs can cause weak combustion, engine hesitation, and a noticeable drop in pickup and efficiency.'
    },
    {
        question: 'Is poor acceleration safe to ignore if the car still drives?',
        answer: 'It is better not to ignore it. Performance loss is often an early sign of a developing fault that could lead to a breakdown or more expensive repairs if left unchecked.'
    }
];

const Blog14 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 14;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5 z-50"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            Why Is My Car Slow to Accelerate? Common Causes and Fixes
                        </h1>
                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            A car that feels slow to accelerate can be frustrating and sometimes dangerous. The cause usually points to fuel delivery, airflow, exhaust restrictions, or sensor issues.
                        </p>
                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated April 2026 &nbsp;|&nbsp; 7 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/14_1.jpeg"
                            alt="Car struggling to accelerate on the road"
                            title="What Causes Poor Acceleration in Cars?"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: car_struggling_acceleration.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#airflow" className="text-gray-900 no-underline hover:underline">Restricted airflow</a></li>
                            <li><a href="#fuel" className="text-gray-900 no-underline hover:underline">Fuel delivery issues</a></li>
                            <li><a href="#ignition" className="text-gray-900 no-underline hover:underline">Ignition system problems</a></li>
                            <li><a href="#transmission" className="text-gray-900 no-underline hover:underline">Transmission or clutch issues</a></li>
                            <li><a href="#checklist" className="text-gray-900 no-underline hover:underline">Inspection checklist</a></li>
                            <li><a href="#dash-section" className="text-gray-900 no-underline hover:underline">How CarFido Assistant helps</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            Most drivers notice poor acceleration in the same way: the car suddenly feels slow pulling away from a stop, weak on an uphill stretch, or hesitant when you try to merge.
                        </p>
                        <p className="mb-0 text-gray-700">
                            Sluggish performance is often an early warning that something in the air, fuel, spark, or transmission side of the system needs attention before it gets more expensive to fix.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>Why is my car slow to accelerate?</strong><br />
                            Common causes include restricted airflow (dirty air filter), weak fuel delivery (fuel pump/injectors), worn ignition parts (spark plugs), or transmission issues like slipping.
                        </p>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/14_2.jpeg"
                            alt="Under-hood inspection for engine performance issues"
                            title="Engine performance inspection"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: engine_performance_inspection.png
                        </figcaption>
                    </figure>

                    {accelerationCauses.map((cause) => (
                        <section key={cause.id} id={cause.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {cause.title}
                            </h2>
                            <p className="mb-0 text-gray-700">{cause.text}</p>
                        </section>
                    ))}

                    <section id="checklist" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Simple Inspection Checklist
                        </h2>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Check the engine air filter for dirt or debris</li>
                            <li>Note if the Check Engine Light is on or flashing</li>
                            <li>Observe if the engine revs (RPM) rise faster than the car accelerates</li>
                            <li>Listen for unusual hissing (vacuum leak) or sputtering sounds</li>
                            <li>Check for heat or smells from the wheel areas (dragging brakes)</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                How CarFido Assistant Helps
                            </h2>
                            <p className="mb-3.5 text-gray-700">
                                Sluggish acceleration can be caused by many different things. CarFido Assistant helps you narrow it down without the guesswork.
                            </p>
                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">CarFido Assistant helps you:</p>
                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Identify likely causes based on how the car behaves under load</li>
                                <li>Follow targeted work plans to check the right components first</li>
                                <li>Provide visibility into likely repair scope and urgency</li>
                                <li>Track recurring performance issues in your vehicle history</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">
                            Frequently Asked Questions
                        </h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                            Final Thoughts
                        </h2>
                        <p className="mb-4 text-gray-700">
                            Poor acceleration is often a progressive issue. In most cars, it starts gradually as airflow, fuel delivery, or spark quality begins to decline.
                        </p>
                        <p className="mb-0 text-gray-700">
                            The sooner you pay attention to the pattern, the easier it is to pinpoint the cause and avoid paying for unnecessary parts or larger repairs later.
                        </p>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#airflow" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Airflow</a></li>
                            <li><a href="#fuel" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Fuel delivery</a></li>
                            <li><a href="#ignition" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Ignition</a></li>
                            <li><a href="#transmission" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Transmission</a></li>
                            <li><a href="#checklist" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Checklist</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog14;
